/*eslint complexity: ["error", 25]*/
/*eslint max-lines-per-function: ["error", 240]*/
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import { Card, Modal } from 'react-bootstrap';
import { Form } from 'react-bootstrap';

import Button from '../../../common/components/Button';
import ValidationAlert from '../../../app/components/ValidationAlert';
import FormRow from '../../../app/components/FormRow';
import {
  CreateUpdateExamTypeDto,
  GetBatteryDto,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ConfirmationButton from '../../../app/components/ConfirmationButton';
import { useNotifications } from '../../../common/hooks/useNotifications';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import CurrencyControl from '../../../app/components/CurrencyControl';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { ExamTypeCategorySelector } from '../Selects/ExamTypeCategorySelector';
import { CustomFieldsSelector } from '../Selects/CustomFieldsSelector';

interface ExamTypeEditorProps {
  id?: string;
  examType: CreateUpdateExamTypeDto;
  handleClose?: () => void;
  show?: boolean;
  batteries: GetBatteryDto[];
  onCompleted: (
    examType: CreateUpdateExamTypeDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const ExamTypeEditor: React.FC<ExamTypeEditorProps> = props => {
  const [examType, setExamType] = useState(props.examType);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const notifications = useNotifications();
  const spd = useSpdCore();

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setExamType(props.examType);
    }
  }, [props.show, props.examType]);

  const createUpdateExamType = async () => {
    setIsSaving(true);
    props
      .onCompleted(examType, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
      })
      .catch(error => {
        console.error('Error during add exam type:', error);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const deleteExamType = async () => {
    if (props.id) {
      notifications.onPromise(
        spd.examTypes.changeToInactive(props.id),
        props.handleClose,
      );
    }
  };

  const getAllBatteries = () => {
    const arr = props.batteries.map(reg => {
      return {
        value: reg.id,
        label: reg.name,
      };
    });
    return arr;
  };

  const getSelectedBattery = () => {
    return getAllBatteries().filter(
      item => item.value === props.examType.batteryId,
    );
  };

  const propertyChange = (obj: Partial<CreateUpdateExamTypeDto>) => {
    setExamType({ ...examType, ...obj });
  };

  return (
    <Modal
      onHide={props.handleClose}
      show={props.show}
      centered={true}
      keyboard={true}
      backdrop="static"
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {props.id ? 'Edycja typu badania' : 'Dodawanie nowego typu badania'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Card className="mb-2">
          <Card.Header>Typ badania</Card.Header>
          <Card.Body>
            <Form className="d-grid gap-3">
              <FormRow controlId="category" label="Kategoria typu badania">
                <ExamTypeCategorySelector
                  value={examType?.categoryId || ''}
                  onChange={e => propertyChange({ categoryId: e.value })}
                />
              </FormRow>
              <FormRow controlId="examType_name" label="Nazwa">
                <Form.Control
                  type="text"
                  value={examType?.name || ''}
                  onChange={e => propertyChange({ name: e.target.value })}
                />
              </FormRow>
              <FormRow controlId="examType_description" label="Opis">
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={examType?.description || ''}
                  onChange={e =>
                    propertyChange({ description: e.target.value })
                  }
                />
              </FormRow>
              <FormRow
                controlId="examType_importantNote"
                label="Ważna informacja dla badanego"
              >
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={examType?.importantNote || ''}
                  onChange={e =>
                    propertyChange({ importantNote: e.target.value })
                  }
                />
              </FormRow>
              <FormRow controlId="examType_price" label="Cena">
                <CurrencyControl
                  value={examType?.price || ''}
                  decimalScale={2}
                  onChange={value => propertyChange({ price: value || null })}
                />
              </FormRow>
              <FormRow controlId="battery" label="Bateria">
                <Select
                  options={getAllBatteries()}
                  defaultValue={getSelectedBattery()}
                  onChange={options =>
                    propertyChange({ batteryId: options?.value })
                  }
                />
              </FormRow>
              <FormRow
                controlId="customFields"
                label="Wymagane przy rejestracji"
              >
                <CustomFieldsSelector
                  value={examType?.customFieldsId || ''}
                  clearable={true}
                  onChange={e => propertyChange({ customFieldsId: e.value })}
                />
              </FormRow>
              <FormRow controlId="user_popular" label="Popularny">
                <Form.Check
                  type="checkbox"
                  checked={examType?.popular ? true : false}
                  onChange={e => propertyChange({ popular: e.target.checked })}
                />
              </FormRow>
              <FormRow controlId="online" label="Dostępny online">
                <Form.Check
                  disabled={
                    process.env.REACT_APP_ENABLE_ONLINE_EXAM_TYPE !== 'true'
                  }
                  type="checkbox"
                  checked={examType?.online ? true : false}
                  onChange={e =>
                    propertyChange({
                      online: e.target.checked,
                      ...(e.target.checked && { onlinePayment: true }),
                    })
                  }
                />
              </FormRow>
              <FormRow controlId="onlinePayment" label="Płatność online">
                <Form.Check
                  disabled={examType?.online ? true : false}
                  type="checkbox"
                  checked={examType?.onlinePayment !== false}
                  onChange={e =>
                    propertyChange({ onlinePayment: e.target.checked })
                  }
                />
              </FormRow>
              <FormRow
                controlId="selectHarmfulFactors"
                label="Wybór czynników szkodliwych"
              >
                <Form.Check
                  type="checkbox"
                  checked={examType?.selectHarmfulFactors ? true : false}
                  onChange={e =>
                    propertyChange({ selectHarmfulFactors: e.target.checked })
                  }
                />
              </FormRow>
              {/* <FormRow controlId="user_popular" label="Aktywny">
                <Form.Check
                  type="checkbox"p
                  readOnly={true}
                  checked={examType?.active ? true : false}
                  onChange={e => propertyChange({ popular: e.target.active })}
                />
              </FormRow> */}
            </Form>
          </Card.Body>
        </Card>
        <ValidationAlert
          show={showSaveErrors}
          errors={saveErrors}
          className="mt-3"
        />
      </Modal.Body>
      <Modal.Footer>
        {props.id && (
          <LastEditorInfo id={props.id} method={spd.examTypes.getLastEditor} />
        )}
        <Button variant="outline-secondary" onClick={props.handleClose}>
          Zamknij
        </Button>
        {props.id && (
          <ConfirmationButton
            className="mx-1"
            confirmation="Czy na pewno usunąć typ badania?"
            variant="outline-danger"
            onOK={deleteExamType}
          >
            Usuń
          </ConfirmationButton>
        )}
        <Button
          variant="outline-primary"
          onClick={createUpdateExamType}
          isLoading={isSaving}
        >
          Zapisz
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
